import {
	Box,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	Pagination,
	Tab,
	Typography,
} from '@mui/material';
import React, { ChangeEvent, DragEvent, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { DeleteIcon, EmptyListIllustration, LockIcon, ModalCloseIcon } from '../Icons';
import SearchBar from '../SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import { useNavigate, useParams } from 'react-router-dom';
import DataNotFound from '../DataItemFound/DataNotFound';
import { IsMobileWidth, IsTabletWidth } from '../MediaQueryUtils';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { truncateField, validateFileSize, validateFileUpload } from '@src/helpers/Helpers';
import { CustomAlertType, PartOptions, ProductCatalog } from '@src/pages/Products';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Filters, Option } from '@src/interfaces/type';
import CustomModal from '../CustomModal';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import UploadCSVIcon from '@src/assets/icons/file-upload.svg';
import DragDrop from '../ImageUploader/DragDrop';
import apiConfig from '@src/config/apiConfig';
import { getStorage } from '@src/helpers/StorageHelper';
import SampleFileButton from '../CustomButton/SampleFileButton';
import { Uploaded } from './Uploaders';
import CustomDrawer from '../CustomDrawer/CustomDrawer';
import CustomTab from '../CustomTab/CustomTab';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import CustomPermission from '../Permissions/CustomPermission';
import { setPageNumber, setProductCatalog } from '@src/store/reducers/catalogReducer';
import ProductCustomCard from '../CustomCard/ProductCustomCard';
import PageHeaderMobile from '../PageHeader/PageHeaderMobile';
import { SelectedItemsTextContainer } from '../CommonComponents/Common';
import CustomTextButton from '../CustomButton/CustomTextButton';
import CategoryCarousel from '../Carousal/CategoryCarousal/CategoryCarousal';
import { Category } from '@src/pages/MarketPlace';
import Loading from '../Loading/Loading';
import CategorySliderSkeleton from '../CustomSkeleton/CategorySliderSkeleton';
import ProductCardSkeleton from '../CustomSkeleton/ProductCardSkeleton';
import ProductCard from '../CustomCard/ProductCard';
import ProductDetails from '../ProductDetails/ProductDetails';
import CustomFilter from '../CustomFilter/CustomFilter';
import ProductList from '../ProductListing/ProductList';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: auto;
	padding: 16px;
	gap: 15px;
	overflow: auto;
	@media screen and (max-width: 600px) {
		gap: 0;
		margin-bottom: 68px;
	}
`;

const StyledDataGridHeader = styled.div`
	height: 75px;
	#gridHeader {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		@media screen and (min-width: 750px) and (max-width: 800px) {
			button {
				width: 220px;
			}
		}
	}
	#textContainer {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 12px;
		@media screen and (max-width: 600px) {
			flex-direction: column;
		}
	}
	@media screen and (max-width: 600px) {
		height: auto;
	}
`;
const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	font-size: 16px;
	font-weight: 750;
	padding: 12px;
	transition: width 0.3s ease;
	@media screen and (max-width: 600px) {
	}
`;

const StyledErrorContent = styled.div`
	margin-top: 10px;
	height: 100%;
	overflow-y: auto;
	ul {
		height: 100%;
		list-style-type: disc;
	}
	#heading {
		font-weight: bold;
		text-decoration: underline;
	}
`;

const StyledErrorMessage = styled.div`
	margin-bottom: 10px;
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#filter-container {
		display: flex;
		gap: 10px;
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
	}
`;

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
	marginBottom: '118px',
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 55px;
`;

const UploadContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 15px;
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 15px;
	width: 100%;
	@media screen and (max-width: 600px) {
		flex-direction: column;
	}
`;

const SampleDataHeading = styled.p`
	color: var(--others-black, #000);
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	letter-spacing: 0.16px;
	span {
		color: var(--color-font-grey);
		font-size: 14px;
		font-weight: 600;
	}
`;

const TableContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;
	width: 30%;
	@media screen and (max-width: 600px) {
		width: 90%;
	}
`;

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

interface MediaQueries {
	'@media (max-width: 600px)': React.CSSProperties;
}

const stylingDeleteModal: React.CSSProperties & MediaQueries = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '306px',
	height: '375px',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

interface FileUploaderProps {
	progress?: number | null;
	file?: File | null;
	handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleFileDrop: (event: DragEvent<HTMLDivElement>) => void;
	validateCSVFile: () => void;
	setFile: React.Dispatch<React.SetStateAction<File | null>>;
	validate: boolean;
	windowDimensions: {
		width: number;
		height: number;
	};
	isTabletWidth: boolean;
	disable?: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({
	progress,
	file,
	handleFileChange,
	handleFileDrop,
	validateCSVFile,
	setFile,
	validate,
	windowDimensions,
	isTabletWidth,
}) => {
	// if (progress && progress < 100) {
	// 	return (
	// 		<UploadContainer>
	// 			<UploadFileProgressLoader style={{ width: '381px' }} percentage={progress} />
	// 		</UploadContainer>
	// 	);
	// }
	if (file) {
		return (
			<>
				<UploadContainer>
					<DragDrop
						handleFileChange={handleFileChange}
						handleFileDrop={handleFileDrop}
						text="Select a CSV file to upload"
						info="Max upload file size: 50MB"
						style={{ width: isTabletWidth ? '95vw' : '1054px', height: '202px' }}
						accept=".csv"
						disable
					/>
				</UploadContainer>
				<Uploaded file={file} setFile={setFile} windowDimensions={windowDimensions} />
			</>
		);
	}
	return (
		<UploadContainer>
			<DragDrop
				handleFileChange={handleFileChange}
				handleFileDrop={handleFileDrop}
				text="Select a CSV file to upload"
				info="Max upload file size: 50MB"
				style={{ width: isTabletWidth ? '95vw' : '1054px', height: '202px' }}
				accept=".csv"
			/>
		</UploadContainer>
	);
};

const UploadCSV: React.FC<FileUploaderProps> = ({
	progress,
	file,
	handleFileChange,
	handleFileDrop,
	validateCSVFile,
	setFile,
	validate,
	windowDimensions,
	isTabletWidth,
	disable = false,
}) => {
	return (
		<>
			<FileUploader
				progress={progress}
				file={file}
				handleFileChange={handleFileChange}
				handleFileDrop={handleFileDrop}
				validateCSVFile={validateCSVFile}
				setFile={setFile}
				validate={validate}
				windowDimensions={windowDimensions}
				isTabletWidth={isTabletWidth}
			/>
			<Divider
				style={{
					width: '100%',
					marginTop: '14px',
					color: '#F1F1F1',
					height: '1.5px',
					position: 'fixed',
					bottom: '64px',
				}}
			/>
			<Box
				width="1054px"
				position="fixed"
				bottom="0"
				display="flex"
				justifyContent="flex-end"
				paddingTop="14px"
				paddingBottom="14px"
				sx={{
					'@media screen and (max-width: 800px)': {
						width: '95vw',
						marginRight: '10px',
					},
				}}
			>
				<CustomButton
					type="submit"
					title={
						validate ? (
							<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
						) : (
							'Upload'
						)
					}
					handleClick={validateCSVFile}
					disabled={!file || disable}
					styles={{
						width: '129px',
						height: '36px',
						borderRadius: '4px',
						padding: '7px 37.5px',
						background: 'var(--color-primary)',
						color: 'var(--default-text-color)',
					}}
				/>
			</Box>
		</>
	);
};

type ProductsListingProps = {
	handleRowClick: any;
	products: ProductCatalog[];
	setProducts: (data: ProductCatalog[]) => void;
	categoryOptions: Option[];
	attributeOptions: Option[];
	taxClassesOptions: Option[];
	stockStatusOptions: Option[];
	machineBrandOptions: Option[];
	modelOptions: Option[];
	displayError: (data: string) => void;
	loadingData: boolean;
	setOperation: (data: string) => void;
	setApiCall: (data: boolean) => void;
	setAlert: (data: CustomAlertType) => void;
	page: number;
	handleChange: any;
	totalPages: number;
	categoryId: any;
	setCategoryId: any;
	setPage: (data: number) => void;
	searchValue: string;
	setSearchValue: (data: string) => void;
	searching: boolean;
	setSearching: (data: boolean) => void;
	selectedItemIds: string[];
	setSelectedItemIds: (data: string[]) => void;
	productCount: number;
	categories: any;
	setCategories: any;
	loadingProducts: any;
	setLoadingProducts: any;
	partOptions: PartOptions | null;
	filters: Filters | null;
	setFilters: (data: Filters) => void;
};

export const CatalogProductsListing: React.FC<ProductsListingProps> = ({
	handleRowClick,
	products,
	setProducts,
	categoryOptions,
	machineBrandOptions,
	brandOptions,
	modelOptions,
	attributeOptions,
	taxClassesOptions,
	stockStatusOptions,
	displayError,
	loadingData,
	setOperation,
	setApiCall,
	setAlert,
	page,
	handleChange,
	totalPages,
	categoryId,
	setCategoryId,
	setPage,
	searchValue,
	setSearchValue,
	searching,
	setSearching,
	selectedItemIds,
	setSelectedItemIds,
	productCount,
	categories,
	setCategories,
	loadingProducts,
	setLoadingProducts,
	partOptions,
	filters,
	setFilters,
}) => {
	console.log({ machineBrandOptions });
	const { apiService }: any = React.useContext(ApplicationContext);
	const navigate = useNavigate();
	const { id } = useParams();
	const [uploadModal, setUploadModal] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [deleteProductModal, setDeleteProductModal] = useState(false);
	const [progress, setProgress] = useState<number | null>(null);
	const [fileErrors, setFileErrors] = useState([]);
	const [errorsModal, setErrorsModal] = useState(false);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [validate, setValidate] = useState(false);
	const [disable, setDisable] = useState(false);
	const [preview, setPreview] = useState([]);
	const [tabValue, setTabValue] = useState('1');
	const [collapse, setCollapse] = useState(false);
	const [errorProducts, setErrorProducts] = useState([]);
	const [detailsMiodal, setDetailsModal] = useState(false);
	const [productDetails, setProductDetails] = useState([]);
	const [activeFilter, setActiveFilter] = useState('');

	const { isDrawerOpen } = useAppSelector((state) => state.UI);
	const isMobileWidth = IsMobileWidth();
	const isTabletWidth = IsTabletWidth();

	const checkPermission = usePermission();
	const canCreateProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.CREATE, id);
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);
	const canUploadCSV = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.CREATE_CSV, id);
	const canDeleteProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.DELETE, id);

	const dispatch = useAppDispatch();

	const gridResponsiveStyles: any = {
		'@media (max-width: 478px)': {
			maxHeight: 'calc(100vh - 300px)',
			minHeight: 'calc(100vh - 300px)',
			paddingBottom: '25px',
		},
	};

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};
	const columns = [
		// {
		// 	field: 'id',
		// 	headerName: 'Product ID',
		// 	width: 100,
		// 	headerAlign: 'center',
		// 	align: 'center',
		// 	renderCell: (params: any) => {
		// 		const productId = params.row.id;
		// 		return <div title={productId}>{truncateField(productId, 32)}</div>;
		// 	},
		// },
		{ field: 'id', headerName: '#', width: 100 },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
						}}
						title={name}
					>
						<img
							style={{ objectFit: 'contain' }}
							src={
								params?.row?.images?.[0]?.image ||
								params?.row?.images?.[0]?.url ||
								params?.row?.image ||
								'/placeholderImage.svg'
							}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		{ field: 'groupCategory', headerName: 'Part Group Category', width: 180 },
		{
			field: 'category',
			headerName: 'Category',
			width: 100,
		},
		{
			field: 'subCategory',
			headerName: 'Sub Category',
			width: 100,
		},
		{ field: 'sku', headerName: 'SKU', width: 100, align: 'left' },

		// { field: 'unit', headerName: 'Unit', width: 100 },
		{ field: 'price', headerName: 'Price (INR)', width: 100, align: 'center' },

		//new col
		{ field: 'brand', headerName: 'Brand', width: 150 },
		{ field: 'partNumber', headerName: 'Part Number', width: 150 },
		{ field: 'partDimension', headerName: 'Part Dimension ID', width: 180 },
		{ field: 'partDiameter', headerName: 'Part Diameter ID', width: 180 },
		{ field: 'partWeight', headerName: 'Part Weight', width: 150 },
		{ field: 'partQuality', headerName: 'Part Quality ID', width: 180 },
		{ field: 'partComposition', headerName: 'Part Composition ID', width: 180 },
		{ field: 'partDesign', headerName: 'Part Design ID', width: 180 },
		{ field: 'partPerformance', headerName: 'Part Performance ID', width: 180 },
	];
	const previewColumns = [
		{
			field: 'id',
			headerName: 'Row',
			filterable: true,
			align: 'center',
			headerAlign: 'center',
			width: 100,
		},
		{
			field: 'name',
			headerName: 'Part Name',
			width: 200,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div style={{}} title={name}>
						{truncateField(name, 32)}
					</div>
				);
			},
		},
		{
			field: 'errors',
			headerName: 'Errors',
			headerAlign: 'center',
			width: 600,
			height: 300,
			renderCell: (params: any) => {
				return (
					<StyledErrorContent>
						<ul>
							{params?.row?.errors?.map((error: any, idx: number) => (
								<li key={idx}>{error}</li>
							))}
						</ul>
					</StyledErrorContent>
				);
			},
		},
	];

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		event?.preventDefault();
		const selectedFile = event.target.files && event.target.files[0];
		if (selectedFile) {
			const validFormat = validateFileUpload(['csv'], selectedFile?.name);
			const validSize = validateFileSize(selectedFile.size);

			if (validFormat && validSize) {
				setFile(selectedFile);
			} else {
				displayError('Invalid file format or size.');
			}
		}
	};

	const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
		event?.preventDefault();
		const selectedFile = event.dataTransfer?.files && event.dataTransfer?.files[0];
		if (selectedFile) {
			const validFormat = validateFileUpload(['csv'], selectedFile?.name);
			const validSize = validateFileSize(selectedFile.size);

			if (validFormat && validSize) {
				setFile(selectedFile);
			} else {
				displayError('Invalid file format or size.');
			}
		}
	};

	const SampleData = () => {
		const [subCategoryOptions, setSubCategoryOptions] = useState<Option[]>([]);
		const [categoryOptionsForGroup, setCategoryOptions] = useState<Option[]>([]);
		const [selectedCategory, setSelectedCategory] = useState('');
		const [selectedGroup, setSelectedGroup] = useState('');

		const attributeColumns = [
			{
				field: 'value',
				headerName: 'Name',
				width: 180,
			},
			{
				field: 'id',
				headerName: 'Atttribute ID',
				width: 100,
			},
		];

		const groupColumns = [
			{
				field: 'value',
				headerName: 'Name',
				width: 180,
			},
			{
				field: 'id',
				headerName: 'Part Group ID',
				width: 100,
			},
		];

		const machineBrandColumns = [
			{
				field: 'value',
				headerName: 'Name',
				width: 180,
			},
			{
				field: 'id',
				headerName: 'Machine Brand ID',
				width: 100,
			},
		];

		const brandColumns = [
			{
				field: 'value',
				headerName: 'Name',
				width: 180,
			},
			{
				field: 'id',
				headerName: 'Brand ID',
				width: 100,
			},
		];

		const modelColumns = [
			{
				field: 'value',
				headerName: 'Name',
				width: 180,
			},
			{
				field: 'id',
				headerName: 'Model ID',
				width: 100,
			},
		];

		const categoryColumn = [
			{
				field: 'value',
				headerName: 'Name',
				width: 150,
			},
			{
				field: 'id',
				headerName: 'Category ID',
				width: 140,
			},
		];

		const subCategoryColumn = [
			{
				field: 'value',
				headerName: 'Name',
				width: 150,
			},
			{
				field: 'id',
				headerName: 'SubCategory ID',
				width: 140,
			},
		];

		const getCategories = (category: any) => {
			apiService?.getCategories(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((option: any) => {
						return { value: option?.name, id: option?.catgeory_id };
					});
					setCategoryOptions(options);
					if (category?.value) {
						setSelectedGroup(category?.value);
					}
				},
				(error: AxiosError) => {
					displayError('Something went wrong!');
				},
				category?.id
			);
		};
		const groupRowClick = (params: any, event: any) => {
			getCategories(params?.row);
		};

		const getSubCategories = (category: any) => {
			apiService?.getSubCategories(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((option: any) => {
						return { value: option?.name, id: option?.sub_category_id };
					});
					setSubCategoryOptions(options);
					if (category?.value) {
						setSelectedCategory(category?.value);
					}
				},
				(error: AxiosError) => {
					displayError('Something went wrong!');
				},
				category?.id
			);
		};
		const categoryRowClick = (params: any, event: any) => {
			getSubCategories(params?.row);
		};
		const handleDownload = () => {
			const link = document.createElement('a');
			link.href = 'https://buyspares-assets-prod.s3.ap-south-1.amazonaws.com/ProductSamples.csv';
			link.download = 'CheeniProducts-SampleFile.csv';
			link.click();
		};

		return (
			<Box display="flex" flexDirection="column" gap="15px" marginTop="16px" width="100%" p={2}>
				<Row>
					<TableContainer>
						<SampleDataHeading>
							Part Group <span>{`(Click row to show category table.)`}</span>
						</SampleDataHeading>
						<CustomDataGrid2
							rows={categoryOptions}
							columns={groupColumns}
							handleRowClick={groupRowClick}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>
							Part Category{' '}
							<span>
								{selectedGroup ? `(Part Group: ${truncateField(selectedGroup, 12)})` : null}
							</span>
							<span>{`(Click row to show subcategory table.)`}</span>
						</SampleDataHeading>
						<CustomDataGrid2
							rows={categoryOptionsForGroup}
							columns={categoryColumn}
							handleRowClick={categoryRowClick}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
							localText={selectedCategory ? 'No Category Exist' : 'Select Part Group'}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>
							Part Sub Category{' '}
							<span>
								{selectedCategory ? `(Part Group: ${truncateField(selectedCategory, 12)})` : null}
							</span>
						</SampleDataHeading>
						<CustomDataGrid2
							rows={subCategoryOptions}
							columns={subCategoryColumn}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
							localText={selectedCategory ? 'No SubCategory Exist' : 'Select Part Category'}
						/>
					</TableContainer>
				</Row>
				<Row>
					<TableContainer>
						<SampleDataHeading>Machine Brand</SampleDataHeading>
						<CustomDataGrid2
							rows={machineBrandOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Model</SampleDataHeading>
						<CustomDataGrid2
							rows={modelOptions}
							columns={modelColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Brand</SampleDataHeading>
						<CustomDataGrid2
							rows={brandOptions}
							columns={brandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
				</Row>
				<Row>
					<TableContainer>
						<SampleDataHeading>Part Dimension</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.dimensionOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Part Diameter</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.diameterOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Part Weight</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.weightOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
				</Row>
				<Row>
					<TableContainer>
						<SampleDataHeading>Part Quality</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.qualityOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Part Composition</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.compositionOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Part Design</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.designOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
				</Row>
				<Row>
					<TableContainer>
						<SampleDataHeading>Part Performance</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.performanceOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
					<TableContainer>
						<SampleDataHeading>Part Ownership</SampleDataHeading>
						<CustomDataGrid2
							rows={partOptions?.ownershipOptions}
							columns={machineBrandColumns}
							handleRowClick={() => {}}
							onRowsSelectionHandler={() => {}}
							checkboxSelection={false}
							style={{ maxHeight: '280px', minHeight: '280px' }}
						/>
					</TableContainer>
				</Row>
				<StyledBottomBtnContainer style={{ bottom: 0 }}>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
						}}
					>
						<CustomButton
							title="Download Sample File"
							handleClick={handleDownload}
							styles={{
								width: '327px',
								height: '40px',
								borderRadius: '4px',
								background: '#FFBE00',
								color: 'var(--default-text-color)',
							}}
						/>
					</div>
				</StyledBottomBtnContainer>
			</Box>
		);
	};

	const uploadCSV = () => {
		setDisable(true);
		if (file) {
			const formData = new FormData();
			formData.append('file', file);

			axios
				.post(`${apiConfig?.url}products/csv`, formData, {
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						Authorization: 'Bearer ' + getStorage().getItem('token'),
						'x-supplier-id': id,
					},
					onUploadProgress: (progressEvent) => {
						const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
						console.log(`Upload Progress: ${progress}%`);
						setProgress(progress);
					},
				})
				.then((response) => {
					console.log('Upload successful:', response.data);
					setProgress(null);
					setUploadModal(false);
					setApiCall(true);
					setFile(null);
					setAlert({ message: 'Bulk Product Created Successfully', severity: 'success' });
					setDisable(false);
					setConfirmDialog(false);
				})
				.catch((error) => {
					console.log('Error uploading file:', error?.response?.data?.message);
					if (Array.isArray(error?.response?.data?.message)) {
						setFileErrors(error?.response?.data?.message);
						setErrorsModal(true);
						setUploadModal(false);
					} else {
						displayError(error?.response?.data?.message);
					}
					setDisable(false);
				});
		}
	};

	const validateCSVFile = () => {
		setValidate(true);
		if (file) {
			const formData = new FormData();
			formData.append('file', file);
			apiService.validateCSV(
				formData,
				(response: AxiosResponse) => {
					setConfirmDialog(true);
					setUploadModal(false);
					setPreview(response?.data?.data);
					setValidate(false);
				},
				(error: AxiosError) => {
					const data = error?.response?.data?.message;
					const previewTable = data?.map((item: any) => {
						return { id: item?.row, name: item?.product?.name, errors: item?.errors };
					});

					setErrorProducts(previewTable);
					setErrorsModal(true);
					setUploadModal(false);
					setValidate(false);
				}
			);
		}
	};

	const tabs = [
		{ label: 'Sample Data', value: '1', content: <SampleData /> },
		{
			label: 'Upload File',
			value: '2',
			content: (
				<UploadCSV
					progress={progress}
					file={file}
					handleFileChange={handleFileChange}
					handleFileDrop={handleFileDrop}
					validateCSVFile={validateCSVFile}
					setFile={setFile}
					validate={validate}
					windowDimensions={windowDimensions}
					isTabletWidth={isTabletWidth}
					disable={disable}
				/>
			),
		},
	];

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
	};

	const handleConfirm = () => {
		apiService?.deleteCatalogsProducts(
			selectedItemIds,
			(response: AxiosResponse) => {
				setAlert({
					message:
						selectedItemIds?.length > 1
							? 'Products Deleted Successfully'
							: 'Product Deleted Successfully',
					severity: 'success',
				});
				setApiCall(true);
				setSelectedItemIds([]);
				setOperation('delete');
			},
			(error: AxiosError) => {
				console.log(error);
				displayError('Something went wrong');
			}
		);
	};

	const onRowsSelectionHandler = (ids: any) => {
		setSelectedItemIds(ids);
	};

	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
		dispatch(setPageNumber(1));
	};

	const handleNo = () => {
		setConfirmDialog(false);
		setFile(null);
	};

	const Header = () => {
		return (
			<>
				<PageHeaderMobile
					title="Upload CSV"
					onClick={() => {
						setUploadModal(false);
					}}
				/>
				<Divider style={{ width: '100%' }} />
			</>
		);
	};

	const deleteProduct = () => {
		handleConfirm();
		setDeleteProductModal(false);
	};

	const handleCategoryChange = (category: Category) => {
		if (categoryId.toString() !== category?.category_id?.toString()) {
			setPage(1);
			setCategoryId(category?.category_id as string);
		}
	};
	const btnView = () => {
		if (selectedItemIds?.length) {
			return (
				<CustomButton
					title="Delete Item(s)"
					handleClick={() => {
						setDeleteProductModal(true);
					}}
					styles={{
						width: '320px',
						height: '40px',
						background: '#F26184 !important',
						color: '#FFFFFF',
						transition: 'background 0.3s ease, color 0.3s ease',
					}}
				/>
			);
		}
		return (
			<>
				{canUploadCSV ? (
					<CustomButton
						title="Upload CSV"
						handleClick={() => setUploadModal(true)}
						styles={{
							width: '136px',
							height: '40px',
							borderRadius: '4px',
							background: 'var(--btn-color-grey)',
							color: 'var(--default-text-color)',
							':hover': {
								background: 'var(--btn-hover-color) !important',
							},
						}}
					/>
				) : (
					<CustomTooltip
						title="You do not have permission to upload CSV. Contact 
							an administrator to get permission."
						placement="bottom"
					>
						<span>
							<CustomButton
								title="Upload CSV"
								disabled
								handleClick={() => {}}
								styles={{
									width: '136px',
									height: '40px',
									borderRadius: '4px',
									background: 'var(--btn-color-grey)',
									color: 'var(--default-text-color)',
									':hover': {
										background: 'var(--btn-hover-color) !important',
									},
								}}
							/>
						</span>
					</CustomTooltip>
				)}
				{canCreateProduct ? (
					<CustomButton
						title="Create Product"
						handleClick={() => navigate(`/suppliers/${id}/productCatalog/add`)}
						disabled={loadingData}
						styles={{
							marginRight: '15px',
							width: '179px',
							height: '40px',
							borderRadius: '4px',
							padding: '7px 12px 7px 12px',
						}}
					/>
				) : (
					<CustomTooltip
						title="You do not have permission to create product. Contact 
									an administrator to get permission."
						placement="bottom"
					>
						<span>
							<CustomButton
								title="Create Product"
								handleClick={() => {}}
								disabled
								styles={{
									marginRight: '15px',
									width: '179px',
									height: '40px',
									borderRadius: '4px',
									padding: '7px 12px 7px 12px',
								}}
							/>
						</span>
					</CustomTooltip>
				)}
			</>
		);
	};
	const handleSpecificProductSelect = (id: any) => {
		const ids: string[] = [];

		//@ts-ignore
		setProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
					ids.push(item?.id);
				}
				if (item?.id === id) {
					return {
						...item,
						isSelection: !item?.isSelection,
					};
				}
				return item;
			});
		});
		//@ts-ignore
		setSelectedItemIds(ids);
	};
	const handleSelectAll = (products: any) => {
		const ids: string[] =
			selectedItemIds?.length === products?.length ? [] : products?.map((item: any) => item?.id);
		//@ts-ignore
		setProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedItemIds?.length === products?.length),
				};
			});
		});
		//@ts-ignore
		setSelectedItemIds(ids);
	};

	const viewDetails = (product: any, e: any) => {
		if (e?.target?.tagName === 'INPUT' || ['editIcon']?.includes(e?.target?.id)) {
			return;
		}

		dispatch(setProductCatalog(product));
		navigate(`/suppliers/${id}/productCatalog/edit/${product?.id}`);
	};

	const loadMore = () => {
		setLoadingProducts(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const showDetails = (product: any) => {
		setProductDetails(product);
		setDetailsModal(true);
	};

	const modals = () => {
		return (
			<>
				{confirmDialog ? (
					<CustomModal
						open={confirmDialog}
						setOpen={setConfirmDialog}
						title="Upload CSV"
						style={stylingDeleteModal}
						children={
							<ConfirmModal
								content={`Are you sure you want to upload ${preview?.length} ${
									preview?.length > 1 ? 'products' : 'product'
								}`}
								image={UploadCSVIcon}
								loading={disable}
								handleClose={handleNo}
								handleConfirm={uploadCSV}
							/>
						}
					/>
				) : null}

				{errorsModal ? (
					<CustomDrawer
						open={errorsModal}
						setOpen={setErrorsModal}
						style={{ width: windowDimensions?.width > 800 ? '1118px' : '100%' }}
						children={
							<Box height="100%">
								<>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="center"
										px={2}
										width="100%"
										height="56px"
									>
										<Typography variant="body1">CSV File Errors</Typography>
										<Box sx={{ cursor: 'pointer' }} onClick={() => setErrorsModal(false)}>
											<ModalCloseIcon />
										</Box>
									</Box>
									<Divider style={{ width: '100%' }} />
								</>
								<Box p={2}>
									<CustomDataGrid2
										rows={errorProducts}
										columns={previewColumns}
										handleRowClick={() => {}}
										onRowsSelectionHandler={() => {}}
										checkboxSelection={false}
										style={{
											height: windowDimensions?.width > 800 ? 'calc(100vh - 100px)' : '450px',
										}}
										// muiClassStyles={{
										// 	' .MuiDataGrid-row': {
										// 		minHeight: '150px !important',
										// 		maxHeight: '150px !important',
										// 	},
										// }}
									/>
								</Box>
							</Box>
						}
					/>
				) : null}

				{uploadModal ? (
					<CustomDrawer
						open={uploadModal}
						setOpen={setUploadModal}
						title="Upload CSV"
						style={{ width: isTabletWidth ? '100%' : '1118px' }}
						header={false}
						children={
							<>
								<Header />
								<CustomTab
									sx={{ marginBottom: '73px' }}
									tabValue={tabValue}
									handleTabChange={handleTabChange}
									tabs={tabs}
								/>
							</>
						}
					/>
				) : null}
				{deleteProductModal ? (
					<CustomModal
						open={deleteProductModal}
						setOpen={setDeleteProductModal}
						title="Remove Items"
						style={stylingDeleteModal}
						children={
							<ConfirmModal
								content={`Deleting ${
									selectedItemIds?.length === 1 ? '1 item' : `${selectedItemIds?.length} items`
								} from the product catalog. Are you sure?`}
								loading={false}
								handleClose={() => setDeleteProductModal(false)}
								handleConfirm={deleteProduct}
							/>
						}
					/>
				) : null}

				{detailsMiodal ? (
					<CustomModal
						open={detailsMiodal}
						setOpen={setDetailsModal}
						header={false}
						style={productDetailsModalStyles}
					>
						{/* @ts-ignore */}
						<ProductDetails data={productDetails} setOpenModal={setDetailsModal} />
					</CustomModal>
				) : null}
			</>
		);
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	if (loadingData) {
		// if (isMobileWidth) {
		// 	return (
		// 		<StyledMobileContainer>
		// 			<PageHeaderMobile
		// 				title="Master Catalog"
		// 				setCollapse={setCollapse}
		// 				collapse={collapse}
		// 				onClick={() => {
		// 					navigate(-1);
		// 				}}
		// 			/>
		// 			<CategorySliderSkeleton />
		// 			<ProductContainer>
		// 				{[1, 2, 3]?.map((item) => {
		// 					return <ProductCardSkeleton key={item} />;
		// 				})}
		// 			</ProductContainer>
		// 		</StyledMobileContainer>
		// 	);
		// }
		return <Loading />;
	}

	// if (isMobileWidth) {
	// 	return (
	// 		<StyledMobileContainer>
	// 			<PageHeaderMobile
	// 				title="Master Catalog"
	// 				setCollapse={setCollapse}
	// 				collapse={collapse}
	// 				onClick={() => {
	// 					navigate(-1);
	// 				}}
	// 			/>
	// 			{collapse ? (
	// 				<Collapse
	// 					in={collapse}
	// 					style={{
	// 						display: 'flex',
	// 						gap: '15px',
	// 						alignItems: 'center',
	// 						flexShrink: 0,
	// 						padding: '16px',
	// 					}}
	// 				>
	// 					<SearchBar
	// 						style={{
	// 							width: '328px',
	// 							height: '36px',
	// 						}}
	// 						value={searchValue}
	// 						onChange={handleSearch}
	// 						isLoading={false}
	// 						autoFocus
	// 					/>
	// 				</Collapse>
	// 			) : null}
	// 			<CategoryCarousel
	// 				categories={categories}
	// 				handleCategoryChange={handleCategoryChange}
	// 				selectedIndex={categoryId}
	// 				setSelectedIndex={setCategoryId}
	// 			/>
	// 			<div id="filter-container">
	// 				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
	// 					{products?.length ? (
	// 						<CustomTextButton
	// 							style={{
	// 								fontSize: '13px',
	// 								fontWeight: 700,
	// 								color: '#FF6120',
	// 								textDecoration: 'none',
	// 								maxWidth: '75px',
	// 								marginTop: '4px',
	// 							}}
	// 							title={selectedItemIds?.length === products?.length ? 'Deselect All' : 'Select All'}
	// 							onClick={() => handleSelectAll(products)}
	// 						/>
	// 					) : null}
	// 					{products?.length ? (
	// 						<SelectedItemsTextContainer
	// 							selectedItemIds={selectedItemIds}
	// 							filteredItems={products}
	// 							items
	// 						/>
	// 					) : null}
	// 				</div>
	// 			</div>
	// 			<Divider style={{ width: '100%' }} />
	// 			{products?.length ? (
	// 				<ProductContainer>
	// 					{products?.map((item: any) => (
	// 						<ProductCustomCard
	// 							key={item?.id}
	// 							//@ts-ignore
	// 							product={item}
	// 							increaseQuantity={() => { }}
	// 							decreaseQuantity={() => { }}
	// 							recommendedQuantity={() => { }}
	// 							isContainCheckbox
	// 							checkBoxSelection={handleSpecificProductSelect}
	// 							viewDetails={viewDetails}
	// 							hideBottom
	// 							showPrice
	// 						/>
	// 					))}
	// 					{products?.length < productCount ? (
	// 						<div
	// 							style={{
	// 								display: 'flex',
	// 								alignItems: 'center',
	// 								justifyContent: 'center',
	// 								padding: '16px',
	// 								flexShrink: 0,
	// 								marginBottom: '10px',
	// 							}}
	// 						>
	// 							{!loadingProducts ? (
	// 								<CustomTextButton
	// 									title="Load More"
	// 									onClick={loadMore}
	// 									disabled={loadingProducts}
	// 								/>
	// 							) : (
	// 								<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
	// 							)}
	// 						</div>
	// 					) : null}
	// 				</ProductContainer>
	// 			) : (
	// 				<div style={{ flexGrow: 1 }}>
	// 					<DataNotFound content="No Products Found" height="auto" />
	// 				</div>
	// 			)}

	// 			<StyledBottomBtnContainer>
	// 				<div style={{ display: 'flex', gap: '10px', padding: '10px', flexShrink: 0 }}>
	// 					{btnView()}
	// 				</div>
	// 			</StyledBottomBtnContainer>
	// 			{modals()}
	// 		</StyledMobileContainer>
	// 	);
	// }

	return (
		<div style={{ height: 'auto', overflow: 'auto' }}>
			{modals()}
			<Container>
				{canViewProduct ? (
					<div style={{ height: 'auto' }}>
						<StyledDataGridHeader>
							<div id="gridHeader">
								<SearchBar
									style={{
										width: '328px',
										height: '36px',
									}}
									value={searchValue}
									onChange={handleSearch}
									isLoading={false}
								/>
								{windowDimensions?.width > 750 ? (
									<div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
										{canUploadCSV ? (
											<CustomButton
												title="Upload CSV"
												handleClick={() => setUploadModal(true)}
												styles={{
													width: '172px',
													height: '36px',
													borderRadius: '4px',
													background: 'var(--btn-color-grey)',
													color: 'var(--default-text-color)',
													':hover': {
														background: 'var(--btn-hover-color) !important',
													},
												}}
											/>
										) : (
											<CustomTooltip
												title="You do not have permission to upload CSV. Contact 
							an administrator to get permission."
												placement="bottom"
											>
												<span>
													<CustomButton
														title="Upload CSV"
														disabled
														handleClick={() => {}}
														styles={{
															width: '172px',
															height: '36px',
															borderRadius: '4px',
															background: 'var(--btn-color-grey)',
															color: 'var(--default-text-color)',
															':hover': {
																background: 'var(--btn-hover-color) !important',
															},
														}}
													/>
												</span>
											</CustomTooltip>
										)}

										{canCreateProduct ? (
											<CustomButton
												title="Create Product"
												handleClick={() => navigate(`/suppliers/${id}/productCatalog/add`)}
												disabled={loadingData}
												styles={{
													marginRight: '15px',
													width: '134px',
													height: '36px',
													borderRadius: '4px',
													padding: '7px 12px 7px 12px',
												}}
											/>
										) : (
											<CustomTooltip
												title="You do not have permission to create product. Contact 
									an administrator to get permission."
												placement="bottom"
											>
												<span>
													<CustomButton
														title="Create Product"
														handleClick={() => {}}
														disabled
														styles={{
															marginRight: '15px',
															width: '134px',
															height: '36px',
															borderRadius: '4px',
															padding: '7px 12px 7px 12px',
														}}
													/>
												</span>
											</CustomTooltip>
										)}
									</div>
								) : null}
							</div>

							<div id="textContainer">
								{selectedItemIds?.length ? (
									<SelectedItemsContainer>
										<Typography>{selectedItemIds?.length} items selected</Typography>
										<IconButton onClick={() => setDeleteProductModal(true)}>
											<DeleteIcon />
										</IconButton>
									</SelectedItemsContainer>
								) : null}

								<SelectedItemsContainer>
									<Typography>Total Items: {productCount}</Typography>
								</SelectedItemsContainer>
							</div>
						</StyledDataGridHeader>

						<CustomFilter
							filters={filters}
							setFilters={setFilters}
							activeFilter={activeFilter}
							setActiveFilter={setActiveFilter}
							setLoading={setSearching}
							filterType="server"
							setPage={setPage}
						/>
						<Divider />

						<ProductList
							//@ts-ignore
							products={products}
							searching={searching}
							loadingProducts={loadingProducts}
							loadMore={loadMore}
							handleSpecificProductSelect={handleSpecificProductSelect}
							showDetails={showDetails}
							style={{ overflow: 'auto', height: 'calc(100vh - 150px)' }}
							totalCount={productCount}
							handleRowClick={handleRowClick}
							showStaticQuantity={true}
						/>
					</div>
				) : (
					<CustomPermission
						icon={<LockIcon />}
						title="We are sorry..."
						paragraph="You do not have permission to view products. Contact 
				an administrator to get permission."
					/>
				)}
			</Container>
		</div>
	);
};
